import type { EmailCampaignId } from 'types/campaign'
import type { Thunk } from 'types/redux'
import {
  autosave,
  autosaveIfItsTime,
} from '@disco/state-management/actions/campaign-editor-actions'
import {
  CAMPAIGN_DETAILS_MARK_DIRTY,
  CAMPAIGN_RECIPIENT_ADD,
  CAMPAIGN_RECIPIENT_CHANGE,
  CAMPAIGN_RECIPIENT_MODE_SELECT,
  CAMPAIGN_RECIPIENT_REMOVE,
} from '@disco/constants/ActionTypes'
import type {
  MatchMode,
  RecipientRule,
} from '@disco/state-management/reducers/email-campaigns/recipient-rules'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
const RULE_SAVE_DELAY_MS = 500
const _ruleSaveTimers: Record<
  EmailCampaignId,
  ReturnType<typeof setTimeout>
> = {}

// a schedule or send saves the rules itself, and a rule save landing after it could undo the schedule
export const clearRuleSaveTimer = (campaignId: EmailCampaignId) => {
  clearTimeout(_ruleSaveTimers[campaignId])
  delete _ruleSaveTimers[campaignId]
}

// the backend rebuilds recipients when rules are saved, so a rule change cannot wait for the 60s autosave
const autosaveRule =
  (action: Record<string, any>): Thunk =>
  async (dispatch, getState) => {
    await dispatch(autosave(action))
    const campaignId = getState().emailCampaigns.campaignEditor.id
    if (!campaignId) return
    // restarted on each change, so typing a rule value saves once
    clearRuleSaveTimer(campaignId)
    // a rule with no value yet matches everyone (or nobody, for a mailing list), so it is left to the 60s autosave
    const { rules } = getState().emailCampaigns.recipientRules
    if (rules.some(rule => !rule.ruleValue)) return
    _ruleSaveTimers[campaignId] = setTimeout(() => {
      delete _ruleSaveTimers[campaignId]
      // another campaign was opened or this one deleted, so marking it unsaved would stick or recreate it
      const state = getState()
      if (
        state.emailCampaigns.campaignEditor.id !== campaignId ||
        !getCampaign(state, { campaignId })
      ) {
        return
      }
      // a save that finished since this change cleared the unsaved mark without including it
      dispatch({ type: CAMPAIGN_DETAILS_MARK_DIRTY, campaignId })
      // performSave has already notified, and the 60s autosave is still set to retry
      Promise.resolve(dispatch(autosaveIfItsTime(campaignId, true))).catch(
        () => {},
      )
    }, RULE_SAVE_DELAY_MS)
  }

/**
 * Selects the match mode for campaign recipient filtering.
 * @param matchMode - Mode for matching recipients (AND/OR logic)
 * @returns Thunk that updates recipient match mode with autosave
 */
export function campaignRecipientModeSelect(matchMode: MatchMode): Thunk {
  return autosaveRule({
    type: CAMPAIGN_RECIPIENT_MODE_SELECT,
    matchMode,
  })
}
/**
 * Adds a new recipient rule to the campaign.
 * @param ruleType - Type of recipient rule to add
 * @returns Thunk that adds recipient rule with autosave
 */
export function campaignRecipientAdd(ruleType: string): Thunk {
  return autosaveRule({
    type: CAMPAIGN_RECIPIENT_ADD,
    ruleType,
  })
}
/**
 * Removes a recipient rule from the campaign.
 * @param ruleId - ID of the recipient rule to remove
 * @returns Thunk that removes recipient rule with autosave
 */
export function campaignRecipientRemove(ruleId: string): Thunk {
  return autosaveRule({
    type: CAMPAIGN_RECIPIENT_REMOVE,
    ruleId,
  })
}
/**
 * Updates an existing recipient rule with new values.
 * @param ruleId - ID of the recipient rule to update
 * @param updated - Partial recipient rule data to update
 * @returns Thunk that updates recipient rule with autosave
 */
export function campaignRecipientChange(
  ruleId: string,
  updated: Partial<RecipientRule>,
): Thunk {
  return autosaveRule({
    type: CAMPAIGN_RECIPIENT_CHANGE,
    ruleId,
    updated,
  })
}
